import {
  EventStreamContentType,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { BEARER_TOKEN_KEY } from "@src/auth/client.constants";
import { IS_IN_IFRAME } from "@src/env";
import useLatest from "@src/hooks/useLatest";
import { getAccessToken } from "@src/okta/tokenManager";
import {
  experimental_streamedQuery as streamedQuery,
  useQuery,
  type QueryKey,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_ATTEMPTS_BEFORE_POLLING = 3;

class FatalSSEError extends Error {}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (IS_IN_IFRAME) {
    const iframeToken = localStorage.getItem(BEARER_TOKEN_KEY);
    if (iframeToken) {
      headers.Authorization = `Bearer ${iframeToken}`;
      return headers;
    }
  }
  const accessToken = await getAccessToken();
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

function parseSSEData<T>(eventName: string, raw: string): T | null {
  if (eventName === "keepalive") {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    console.error(`[useSSE] Failed to parse "${eventName}" event`, {
      rawData: raw,
      error: err,
    });
    return null;
  }
}

export interface SSEEvent<TEventMap extends Record<string, unknown>> {
  type: keyof TEventMap & string;
  data: TEventMap[keyof TEventMap];
}

export type SSEEventHandler<
  TEventMap extends Record<string, unknown>,
  TKey extends keyof TEventMap = keyof TEventMap,
> = (data: TEventMap[TKey]) => void;

export type SSEEventHandlers<TEventMap extends Record<string, unknown>> = {
  [K in keyof TEventMap]?: SSEEventHandler<TEventMap, K>;
};

export interface UseSSEOptions<TEventMap extends Record<string, unknown>> {
  url: string;
  queryKey: QueryKey;
  handlers: SSEEventHandlers<TEventMap>;
  enabled?: boolean;
  retryDelay?: number;
}

export interface UseSSEResult {
  isFallbackPolling: boolean;
}

export function useSSE<TEventMap extends Record<string, unknown>>(
  options: UseSSEOptions<TEventMap>,
): UseSSEResult {
  const { url, queryKey, handlers, enabled = true, retryDelay } = options;

  const [isFallbackPolling, setIsFallbackPolling] = useState(false);

  const mountedRef = useRef(true);

  const handlersRef = useLatest(handlers);

  const reconnectAttemptsRef = useRef(0);

  const pendingReconnectRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const onFallbackRef = useRef(() => {
    if (mountedRef.current) {
      setIsFallbackPolling(true);
    }
  });

  const stableQueryKey = JSON.stringify(queryKey);

  const streamFn = useMemo(() => {
    return streamedQuery({
      streamFn: async ({ signal }) => {
        const queue: SSEEvent<TEventMap>[] = [];
        let notify: (() => void) | null = null;
        let done = false;

        const push = (event: SSEEvent<TEventMap>) => {
          queue.push(event);
          notify?.();
          notify = null;
        };

        const scheduleReconnect = (startConnect: () => void) => {
          const attempt = ++reconnectAttemptsRef.current;
          if (attempt >= MAX_RECONNECT_ATTEMPTS_BEFORE_POLLING) {
            console.warn(
              "[useSSE] Max reconnect attempts reached, falling back to polling",
              { url, attempts: attempt },
            );
            onFallbackRef.current();
            reconnectAttemptsRef.current = 0;
            pendingReconnectRef.current = setTimeout(
              startConnect,
              retryDelay ?? MAX_RECONNECT_DELAY_MS,
            );
            return;
          }
          const delay =
            retryDelay ??
            Math.min(
              INITIAL_RECONNECT_DELAY_MS * 2 ** (attempt - 1),
              MAX_RECONNECT_DELAY_MS,
            );
          pendingReconnectRef.current = setTimeout(startConnect, delay);
        };

        const startConnect = () => {
          console.log("[useSSE] Starting SSE connection");

          if (signal.aborted) {
            done = true;
            notify?.();
            return;
          }

          void (async () => {
            let headers: Record<string, string>;
            try {
              headers = await getAuthHeaders();
            } catch (err) {
              console.error("[useSSE] Failed to resolve auth headers", { err });
              scheduleReconnect(startConnect);
              return;
            }

            try {
              await fetchEventSource(url, {
                signal,
                headers,
                openWhenHidden: true,
                async onopen(response) {
                  if (
                    response.ok &&
                    response.headers
                      .get("content-type")
                      ?.startsWith(EventStreamContentType)
                  ) {
                    console.log("[useSSE] SSE opened");
                    return;
                  }
                  if (response.status >= 400 && response.status < 500) {
                    throw new FatalSSEError(
                      `SSE rejected with ${response.status}`,
                    );
                  }
                  throw new Error(`SSE bad response ${response.status}`);
                },
                onmessage(msg) {
                  if (!msg.event) return;

                  const eventName = msg.event as keyof TEventMap & string;
                  const data = parseSSEData<TEventMap[typeof eventName]>(
                    eventName,
                    msg.data,
                  );
                  if (data == null) return;

                  const handler = handlersRef.current[eventName];
                  if (handler) {
                    (handler as SSEEventHandler<TEventMap, typeof eventName>)(
                      data,
                    );
                  }

                  push({ type: eventName, data });
                },
                onerror(err) {
                  throw err;
                },
              });
            } catch (err) {
              if (signal.aborted) {
                done = true;
                notify?.();
                return;
              }
              console.error("[useSSE] SSE connection error", { url, err });
              if (err instanceof FatalSSEError) {
                onFallbackRef.current();
                done = true;
                notify?.();
                return;
              }
              scheduleReconnect(startConnect);
            }
          })();
        };

        signal.addEventListener("abort", () => {
          console.log("[useSSE] SSE connection aborted");
          if (pendingReconnectRef.current) {
            clearTimeout(pendingReconnectRef.current);
            pendingReconnectRef.current = null;
          }
          done = true;
          notify?.();
        });

        startConnect();

        return {
          [Symbol.asyncIterator]() {
            return {
              async next() {
                while (queue.length === 0 && !done) {
                  await new Promise<void>((resolve) => {
                    notify = resolve;
                  });
                }
                if (queue.length > 0) {
                  return { value: queue.shift()!, done: false as const };
                }
                return { value: undefined as never, done: true as const };
              },
              async return() {
                done = true;
                return { value: undefined as never, done: true as const };
              },
            };
          },
        };
      },
      refetchMode: "reset",
      reducer: (acc: SSEEvent<TEventMap>[], event: SSEEvent<TEventMap>) => [
        ...acc,
        event,
      ],
      initialValue: [] as SSEEvent<TEventMap>[],
    });
  }, [url, retryDelay, stableQueryKey, enabled]);

  useQuery({
    queryKey: enabled ? queryKey : ["sse-disabled"],
    enabled,
    queryFn: streamFn,
    staleTime: Infinity,
    refetchOnMount: false,
    gcTime: 0,
  });

  // When url/queryKey/enabled change, a new streamFn is produced. The old
  // attempt counter and fallback state belong to the previous connection cycle
  // and must not bleed into the new one.
  useEffect(() => {
    reconnectAttemptsRef.current = 0;

    if (mountedRef.current) {
      setIsFallbackPolling(false);
    }

    return () => {
      // Cancel any pending reconnect timer from the previous streamFn cycle
      // so it can't fire and open a ghost connection after recreation.
      if (pendingReconnectRef.current) {
        clearTimeout(pendingReconnectRef.current);
        pendingReconnectRef.current = null;
      }
    };
  }, [streamFn]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (mountedRef.current) {
        setIsFallbackPolling(false);
      }
      reconnectAttemptsRef.current = 0;
    }

    return () => {
      reconnectAttemptsRef.current = 0;
    };
  }, [enabled]);

  return { isFallbackPolling };
}
